/**
 * @file   src\Pages\AddSubscription.tsx
 * @brief  AddSubscription page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { useState, useEffect, Container, Badge, Button, Breadcrumb, Row, Col, useOutletContext, useNavigate, useParams, React } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWTextarea from '../components/CWTextarea';
import { pageURLs } from '../utils/pageUrls';
import * as Yup from 'yup'
import { MessageToaster } from '../utils/toastUtil';
import { DISCOUNT_MAX_LENGTH, PROMO_CODE_MAX_LENGTH, PROMOTION_DETAIL_MAX_LENGTH, PROMOTION_NAME_MAX_LENGTH } from '../utils/constants';
import { AppContextType } from '../interfaces/generalInterface';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import GenerateLink from '../components/GenerateLink';
import { DiscountType, userType } from '../utils/enums';
import { addPromotionSchema } from '../validations/addPromotionSchema';
import { CompanyApi, LocationApi, PromotionApi, SubscriptionApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import { LocationItem, Option } from '../interfaces/subscriptionInterface';
import { SubscriptionOption } from '../interfaces/CarWashInterface';
import CWInputGroup from '../components/CWInputGroup';
import SmallLoader from '../components/SmallLoader';
import { PromotionItem } from '../interfaces/promotionInterface';
import moment from "moment-timezone"
import { CommonMessages } from "../messages/validationMessages"

const AddPromotion = (props: any) => {

    const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false;
    // State to company details
    const [company, setCompany] = useState({
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: ""
    });

    const durationOpions = [
        { label: 'Select', value: 0 },
        { label: 'One Month', value: 1 },
        { label: 'Two Months', value: 2 },
        { label: 'Three Months', value: 3 },
        { label: 'Four Months', value: 4 },
        { label: 'Five Months', value: 5 },
        { label: 'Six Months', value: 6 },
        { label: 'Seven Months', value: 7 },
        { label: 'Eight Months', value: 8 },
        { label: 'Nine Months', value: 9 },
        { label: 'Ten Months', value: 10 },
        { label: 'Eleven Months', value: 11 },
        { label: 'Twelve Months', value: 12 },
    ];

    // State to handle locations given in dropdown
    const [locations, setLocations] = useState<Option[]>([]);

    const [washNames, setWashNames] = useState<Option[]>([]);

    // Convert ConnectivityType enum to an array of options
    const discountOptions = [
        { label: 'Select', value: 0 }, // First option with value 0
        ...Object.keys(DiscountType).filter(key => !isNaN(Number(key))).map(key => ({
            label: DiscountType[key as keyof typeof DiscountType],
            value: Number(key)
        }))
    ];


    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const params = useParams();


    // Initialize states for formdata
    const [formData, setFormData] = useState<PromotionItem>({
        assignedlocation: null,
        washname: null,
        promotionname: '',
        generatecode: '',
        discounttype: null,
        discount_value: null,
        validFrom: '',
        validTo: '',
        promotiondetails: '',
        PromotionDuration: 0

    })

    // Initialise states for errors
    const [errors, setErrors] = useState({
        assignedlocation: '',
        washname: '',
        promotionname: '',
        generatecode: '',
        discounttype: '',
        discount_value: '',
        validFrom: '',
        validTo: '',
        promotiondetails: '',
        PromotionDuration: ''

    });

    const [loading, setLoading] = useState(true);

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    const navigate = useNavigate()

    const toaster = new MessageToaster();

    const [companyDetails, setCompanyDetails] = useState({});
    const [subscriptionActive, setSubscriptionActive] = useState(0);


    useEffect(() => {
        getCompanyAddress()
        getCompany()
        // To get details by id
        if (props.edit) {
            getPromotionDetails()
        }
    }, [])


    // To display heading
    useEffect(() => {
        handleUpdate()
    }, [company])

    // Fetch washname options based on assigned location
    useEffect(() => {
        if (formData.assignedlocation) {

            getWashnames()
        }
    }, [formData.assignedlocation]);  // Fetch wash names when assigned location changes


    // To get company details
    const getCompanyAddress = async () => {
        try {
            const id = params.id ? parseInt(params.id) : 0;
            const { data } = (!carwash_owner) ? await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS);
            if (data?.error === 0) {
                setCompany(data.data);
            }
            setLoading(false)
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Function to fetch company information
    const getCompany = async () => {
        try {
            const param1 = `${params.id}`;
            const { data } = (!carwash_owner) ?
                await axios.get(CompanyApi.GET_COMPANY + param1) :
                await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);
            setCompanyDetails(data.data)
            setSubscriptionActive(data.data.subscriptionActive);
            // To get locations under company
            await getAssignedLocations(data.data.carWashGUID)

        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };

    // Function get subscriptions in different locations
    const getWashnames = async () => {
        try {
            const { data } = (!carwash_owner) ?
                await axios.get(SubscriptionApi.GET_SUBSCRIPTION_LIST_BY_LOCATION + formData.assignedlocation)

                : await axios.get(SubscriptionApi.CARWASH_OWNER_GET_SUBSCRIPTION_LIST_BY_LOCATION + formData.assignedlocation)
            if (data?.error === 0) {
                const wash_list: SubscriptionOption[] = data?.data

                if (wash_list) {
                    const transformedArray: Option[] = [
                        ...wash_list.map(item => ({
                            value: item.subscriptionID,
                            label: item.washName
                        }))
                    ];

                    setWashNames(transformedArray);
                }
            } else {
                setWashNames([])
            }

        } catch (error: any) {
            console.error('Error fetching wash names:', error);
            throw new Error(error.response?.data?.message || error.message);

        }
    };

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: props.userType !== userType["CARWASH-OWNER"] ? company.companyName : props.edit ? "Edit Promotion" : "Add New Promotions", url: props.userType !== userType["CARWASH-OWNER"] ? `/superadmin/${pageURLs.manage_promotions}/${params.id}` : ``, showBackButton: props.userType !== userType["CARWASH-OWNER"] ? true : false, showMainMenu: true, activeLink: props.userType !== userType["CARWASH-OWNER"] ? "manage-car-wash" : "" });
    };

    // Function to get Locations
    const getAssignedLocations = async (carwashGuid: string) => {
        try {

            const { data } = (!carwash_owner) ?
                await axios.get(LocationApi.GET_COMPANY_LOCATIONS + carwashGuid)
                : await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS);
            if (data.error === 0) {
                const location_list: LocationItem[] = data?.data

                // Transform the array
                const transformedArray: Option[] = location_list.map(item => ({
                    label: item.locationName,
                    value: item.locationId,
                }));

                setLocations(transformedArray)
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    const getPromotionDetails = async () => {
        try {


            const { data } = (!carwash_owner) ?
                await axios.get(PromotionApi.GET_PROMOTION_DETAILS + params.id + '/' + params.promotionId)
                :
                await axios.get(PromotionApi.CARWASH_OWNER_GET_PROMOTION_DETAILS + '/' + params.promotionId)
            if (data.error === 0) {
                const promotion = data.data

                setFormData({
                    assignedlocation: promotion.locationId,
                    washname: promotion.subscriptionID,
                    promotionname: promotion.promotionName,
                    generatecode: promotion.promotionCode,
                    discounttype: promotion.discountType,
                    discount_value: promotion.discountValue,
                    validFrom: moment(promotion.validFrom).format('MM/DD/YYYY'),
                    validTo: moment(promotion.validTo).format('MM/DD/YYYY'),
                    promotiondetails: promotion.promotionDetails,
                    PromotionDuration: promotion.promotionDuration,
                })

            }
            else {
                // show error message
                toaster.toastError(data.message);
            }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Triggers when input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = event.target

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        if (name === 'discount_value') {
            // Regex to match the input value with up to 2 decimal places
            const regex = /^\d{0,8}(\.\d{0,2})?$/;

            if (regex.test(value)) {
                setFormData({
                    ...formData,
                    [name]: parseInt(value),
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }
    }

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value,
        });

    };

    const handleDateChange = (setter: React.Dispatch<React.SetStateAction<Date | null>>, name: string) => (date: Date | null) => {
        setter(date);

        // To remove validation when user enters input
        const keys = Object.keys(errors);

        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: date,
        });
    };

    // Triggers on submit
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        try {
            const locale = 'en-us';

            // Validate form data
            await addPromotionSchema.validate(formData, { abortEarly: false });

            try {
                setLoading(true)
                const fromDate = new Date(formData.validFrom,).toLocaleDateString(locale);
                const toDate = new Date(formData.validTo,).toLocaleDateString(locale);
                const fromDateArray = fromDate.split('/');
                const toDateArray = toDate.split('/')
                // In case of edit
                if (props.edit) {
                    const updatedformData = {
                        locationId: formData.assignedlocation,
                        promotionId: params.promotionId ? parseInt(params.promotionId) : 0,
                        promotionName: formData.promotionname,
                        validFrom: fromDateArray[2] + '-' + fromDateArray[0] + '-' + fromDateArray[1],
                        validTo: toDateArray[2] + '-' + toDateArray[0] + '-' + toDateArray[1],
                        promotionDetails: formData.promotiondetails,
                        promotionDuration: formData.PromotionDuration
                    }


                    const { data } = (!carwash_owner) ?
                        await axios.post(PromotionApi.EDIT_PROMOTION, updatedformData) :
                        await axios.post(PromotionApi.CARWASH_OWNER_EDIT_PROMOTION, updatedformData)

                    if (data?.error === 0) {
                        if (!carwash_owner) {
                            navigate(`/superadmin/${pageURLs.manage_promotions}/${params.id}`)
                        } else {
                            navigate(`/${pageURLs.manage_promotions}`)
                        }

                    }
                    else {
                        // show error message
                        toaster.toastError(data?.message);
                    }

                } else {
                    const newformData = (!carwash_owner) ? {
                        locationId: formData.assignedlocation,
                        subscriptionId: formData.washname,
                        promotionName: formData.promotionname,
                        promoCode: formData.generatecode,
                        discountType: formData.discounttype,
                        discount: formData.discount_value,
                        validFrom: fromDateArray[2] + '-' + fromDateArray[0] + '-' + fromDateArray[1],
                        validTo: toDateArray[2] + '-' + toDateArray[0] + '-' + toDateArray[1],
                        promotionDetails: formData.promotiondetails,
                        PromotionDuration: Number(formData.PromotionDuration)
                    } : {
                        locationId: formData.assignedlocation,
                        subscriptionId: formData.washname,
                        promotionName: formData.promotionname,
                        promoCode: formData.generatecode,
                        discountType: formData.discounttype,
                        discount: formData.discount_value,
                        validFrom: fromDateArray[2] + '-' + fromDateArray[0] + '-' + fromDateArray[1],
                        validTo: toDateArray[2] + '-' + toDateArray[0] + '-' + toDateArray[1],
                        promotionDetails: formData.promotiondetails,
                        carWashId: params.id,
                        PromotionDuration: Number(formData.PromotionDuration)
                    }

                    const { data } = (!carwash_owner) ?
                        await axios.post(PromotionApi.CREATE_PROMOTION, newformData)
                        :
                        await axios.post(PromotionApi.CARWASH_OWNER_CREATE_PROMOTION, newformData)
                    if (data?.error === 0) {
                        if (!carwash_owner) {
                            navigate(`/superadmin/${pageURLs.manage_promotions}/${params.id}`)
                        } else {
                            navigate(`/${pageURLs.manage_promotions}`)
                        }

                    }
                    else {
                        // show error message
                        toaster.toastError(data?.message);
                    }

                }

            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }
            finally {
                setLoading(false); // Hide loader
            }
        } catch (error) {
            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                const newErrors: any = {};
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        }
    }

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    const generateCode = () => {
        //const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const characters = '0123456789';
        let promoCode = '';
        for (let i = 0; i < PROMO_CODE_MAX_LENGTH; i++) {
            const randomIndex = Math.floor(Math.random() * characters.length);
            promoCode += characters[randomIndex];
        }

        // update formData with generated code
        setFormData(prevState => ({
            ...prevState,
            generatecode: promoCode,
        }));

        setErrors({
            ...errors,
            ['generatecode']: ""
        })
    }

    return (

        <div className='manage-carwash-container manage-subscription-container'>
            <Container>
                {props.userType !== userType["CARWASH-OWNER"] ?
                    <Breadcrumb>
                        <Breadcrumb.Item onClick={() => {
                            const url = props.userType !== userType["CARWASH-OWNER"]
                                ? `/superadmin/${pageURLs.manage_carwash}`
                                : pageURLs.manage_carwash
                            itemClick(url)
                        }}>
                            Manage Car Washes
                        </Breadcrumb.Item>

                        <Breadcrumb.Item onClick={() => {
                            const url = props.userType !== userType["CARWASH-OWNER"]
                                ? `/superadmin/${pageURLs.carwash_locations}/${params.id}`
                                : `${pageURLs.carwash_locations}/${params.id}`
                            itemClick(url)
                        }}  > {company.companyName}
                        </Breadcrumb.Item>

                        <Breadcrumb.Item onClick={() => {
                            const url = props.userType !== userType["CARWASH-OWNER"]
                                ? `/superadmin/${pageURLs.manage_promotions}/${params.id}`
                                : pageURLs.manage_promotions
                            itemClick(url)

                        }}>Manage Promotions
                        </Breadcrumb.Item>

                        <Breadcrumb.Item active>{props.edit ? "Edit Promotion" : "Add New Promotion"}</Breadcrumb.Item>
                    </Breadcrumb>
                    : null}

                {props.userType !== userType["CARWASH-OWNER"] ?
                    <div className="manage-activity-wrap d-flex justify-content-between align-items-center">
                        <GenerateLink id={params.id} companyDetails={companyDetails} />
                        <div className="activity-status d-flex">
                            {/* <CarWashStatus carWashId={1} status={true} companyName={"abc"} /> */}
                            <div className="stat-group d-flex align-items-center">
                                <p>Subscription Status</p>
                                <Badge className={subscriptionActive === 1 ? 'active' : 'inactive'}>
                                    {subscriptionActive === 1 ? 'Active' : (subscriptionActive === 0) ? 'Inactive' : 'N/A'}
                                </Badge>
                            </div>

                        </div>
                    </div> : null}

                <div className='cw-form-outer-wrap'>
                    <form onSubmit={(e) => handleSubmit(e)}>
                        <div className="cw-form-container">
                            <div className="form-inner mx-auto">
                                <Row>
                                    <Col md={6}>
                                        <CWSelect
                                            id="assignedlocation"
                                            name="assignedlocation"
                                            options={locations}
                                            label="Assign Location"
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Assign Location"
                                            multi={false}
                                            values={locations.filter(option => option.value === formData.assignedlocation)}
                                            errorMessage={errors.assignedlocation}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'assignedlocation')}
                                            isMandatory
                                            disabled={props.edit ? true : false}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWSelect
                                            id="washname"
                                            name="washname"
                                            options={washNames}
                                            label="Wash Name"
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Wash Name"
                                            multi={false}
                                            values={washNames && washNames.filter(option => option.value === formData.washname)}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'washname')}
                                            errorMessage={errors.washname}
                                            disabled={props.edit ? true : false}
                                            isMandatory
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="promotionname"
                                            name="promotionname"
                                            type="text"
                                            label="Promotion Name"
                                            value={formData.promotionname}
                                            maxLength={PROMOTION_NAME_MAX_LENGTH}
                                            errorMessage={errors.promotionname}
                                            isMandatory
                                            onChange={handleInputChange}
                                            placeholder='Enter Promotion Name'
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <div className="promo-group position-relative">
                                            <CWInput
                                                id="generatecode"
                                                name="generatecode"
                                                type="text"
                                                label="Generate Promo Code"
                                                value={formData.generatecode}
                                                maxLength={PROMO_CODE_MAX_LENGTH}
                                                errorMessage={errors.generatecode}
                                                onChange={handleInputChange}
                                                readOnly
                                                isMandatory
                                            />
                                            {!props.edit ? <Button title="Generate Code" variant="outline-primary" className="outline-btn f-medium" onClick={() => generateCode()}>Generate Code</Button> : ''}
                                        </div>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={3}>
                                        <CWSelect
                                            id="discounttype"
                                            name="discounttype"
                                            options={discountOptions}
                                            label="Discount Type"
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Discount Type"
                                            errorMessage={errors.discounttype}
                                            multi={false}
                                            disabled={props.edit ? true : false}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'discounttype')}
                                            className="no-input"
                                            values={discountOptions.filter(option => option.value === formData.discounttype)}
                                            isMandatory
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <CWInputGroup
                                            id="discount_value"
                                            name="discount_value"
                                            type="number"
                                            label="Discount Value"
                                            isMandatory
                                            readOnly={props.edit}
                                            maxLength={DISCOUNT_MAX_LENGTH}
                                            value={formData.discount_value}
                                            errorMessage={errors.discount_value}
                                            onChange={handleInputChange}
                                            placeholder={formData.discounttype === 1 ? "$" : formData.discounttype === 2 ? "%" : 'Enter Discount Value'}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWSelect
                                            id="duration"
                                            name="PromotionDuration"
                                            options={durationOpions}
                                            label="Duration"
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Duration"
                                            multi={false}
                                            isMandatory
                                            className="no-input"
                                            errorMessage={errors.PromotionDuration}
                                            disabled={props.edit}
                                            values={durationOpions.filter(option => option.value === formData.PromotionDuration)}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'PromotionDuration')}
                                        />
                                        <p className='promo-note mb-3'>Note: {CommonMessages.promotion_duration_note} </p>
                                    </Col>

                                    <Col md={12}>
                                        <Row>
                                            <Col md={6}>
                                                <div className="form-group date-space">
                                                    <p className="label">Valid From<span className="mandatory">*</span></p>
                                                    <DatePicker
                                                        showIcon
                                                        selected={startDate}
                                                        onChange={handleDateChange(setStartDate, 'validFrom')}
                                                        placeholderText='MM/DD/YYYY'
                                                        id='validFrom'
                                                        name='validFrom'
                                                        value={formData.validFrom}
                                                    />
                                                    {errors.validFrom && <span className="error-msg">{errors.validFrom}</span>}

                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="form-group date-space">

                                                    <p className="label">Valid To<span className="mandatory">*</span></p>
                                                    <DatePicker
                                                        showIcon
                                                        selected={endDate}
                                                        onChange={handleDateChange(setEndDate, 'validTo')}
                                                        placeholderText='MM/DD/YYYY'
                                                        id='validTo'
                                                        name='validTo'
                                                        value={formData.validTo}
                                                        minDate={startDate || undefined}
                                                    />
                                                    {errors.validTo && <span className="error-msg">{errors.validTo}</span>}

                                                </div>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                <CWTextarea
                                    id="promotiondetails"
                                    name="promotiondetails"
                                    type="text"
                                    label="Promotion Details"
                                    errorMessage={errors.promotiondetails}
                                    maxLength={PROMOTION_DETAIL_MAX_LENGTH}
                                    onChange={handleInputChange}
                                    value={formData.promotiondetails}
                                    isMandatory
                                />
                            </div>
                        </div>
                        <div className="btn-sec d-flex justify-content-end">
                            <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => props.userType !== userType["CARWASH-OWNER"] ? navigate(`/superadmin/${pageURLs.manage_promotions}/${params.id}`) : navigate(`/${pageURLs.manage_promotions}`)}>Cancel</Button>
                            <Button title="Save" variant="primary" className="primary-btn" type="submit">Save</Button>
                        </div>
                    </form>
                </div>
            </Container >
            {loading && <SmallLoader />}

        </div >
    )
}

export default AddPromotion;
